import { useVisitorContext } from "@/contexts/VisitorTrackingContext";
import { X, Sparkles, ExternalLink } from "lucide-react";
import { useRef, useEffect, useState } from "react";
import { usePersonaLabels, PERSONA_LABELS_FALLBACK } from "@/hooks/usePersonaLabels";

// Static fallback map (used by other modules that import this synchronously).
const PERSONA_LABELS: Record<string, { label: string; icon: string }> = Object.fromEntries(
  PERSONA_LABELS_FALLBACK.map((r) => [r.key, { label: r.label, icon: r.icon }]),
);

export function PersonaBanner() {
  const { persona, setPersona, dynamicMode, profile } = useVisitorContext();
  const { map: personaMap } = usePersonaLabels();
  const [isNew, setIsNew] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  // Clear "new" entrance animation after it plays
  useEffect(() => {
    if (isNew) {
      timerRef.current = setTimeout(() => setIsNew(false), 2000);
      return () => clearTimeout(timerRef.current);
    }
  }, [isNew]);

  const entranceClass = isNew
    ? "animate-fade-in"
    : "";

  const dispatchRetune = (personaLabel: string, industry?: string) => {
    window.dispatchEvent(
      new CustomEvent("tanya:retune", {
        detail: { personaLabel, industry },
      }),
    );
  };

  // Dynamic Mode + Partner variant
  if (dynamicMode && persona === "partner") {
    return (
      <div className={`bg-[hsl(216_60%_96%)] border-b border-[hsl(var(--tango-medium-blue)/0.4)] py-1.5 px-4 text-center text-sm flex flex-wrap items-center justify-center gap-x-3 gap-y-1 ${entranceClass}`}>
        <Sparkles className="w-3.5 h-3.5 text-[hsl(var(--tango-medium-blue))]" />
        <span className="text-foreground">
          <strong className="text-foreground">Partner Mode Active</strong>
        </span>
        <a
          href="https://exchange.tango-networks.com"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-semibold text-[hsl(var(--tango-medium-blue))] hover:underline inline-flex items-center gap-0.5"
        >
          Partner Portal <ExternalLink className="w-3 h-3" />
        </a>
        <button
          onClick={() => dispatchRetune("Channel Partner")}
          className="text-xs text-muted-foreground hover:text-foreground hover:underline"
        >
          Not a partner?
        </button>
      </div>
    );
  }

  // Dynamic Mode + Customer variant
  if (dynamicMode && persona && personaMap[persona]) {
    const roleLabel = personaMap[persona].label;
    const industry = profile.industry;
    return (
      <div className={`bg-[hsl(36_70%_95%)] border-b border-accent/50 py-1.5 px-3 sm:px-4 text-center text-xs sm:text-sm flex flex-wrap items-center justify-center gap-x-2 sm:gap-x-3 gap-y-0.5 ${entranceClass}`}>
        <span className="inline-flex items-center gap-1.5">
          <Sparkles className={`w-3.5 h-3.5 text-accent ${isNew ? "animate-pulse" : ""}`} />
          <strong className="text-foreground">Tailored for you</strong>
        </span>
        <span className="text-foreground">
          <span className="hidden sm:inline">— </span>
          {roleLabel}{industry ? ` · ${industry}` : ""}
        </span>
        <button
          onClick={() => dispatchRetune(roleLabel, industry || undefined)}
          className="text-xs font-semibold text-accent hover:underline min-h-[32px] sm:min-h-0 px-1"
        >
          Change
        </button>
      </div>
    );
  }

  // Standard persona banner (non-dynamic)
  if (!persona || !personaMap[persona]) return null;

  const { label, icon } = personaMap[persona];

  return (
    <div className={`bg-muted border-b border-border py-1.5 px-3 sm:px-4 text-center text-xs sm:text-sm flex flex-wrap items-center justify-center gap-x-2 sm:gap-x-3 gap-y-0.5 ${entranceClass}`}>
      <span className="text-foreground">
        {icon} Showing content tailored for <strong className="text-foreground">{label}</strong>
      </span>
      <button
        onClick={() => dispatchRetune(label, profile.industry || undefined)}
        className="text-xs font-semibold text-accent hover:underline"
      >
        Change
      </button>
      <button
        onClick={() => setPersona(null)}
        className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-0.5"
      >
        <X className="w-3 h-3" />
        Reset
      </button>
    </div>
  );
}

export { PERSONA_LABELS };
